import { Button } from "@/components/ui/button";
import { useSearch } from "@/context/SearchContext";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Sparkles,
  Target,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import arrow from "../../assets/images/arrow.webp";
import {
  parseIntent,
  seachApi,
  startSession,
} from "../../Redux/ReduxSlices/newAppoloApiSlice";
import {
  fetchCompanies,
  fetchPeople,
  fetchPromptSuggestions,
} from "../../Redux/ReduxSlices/newsearchSlice";
import { useAppContext } from "../../context/AppContext";
import {
  apolocompanyPerPage,
  apolopeoplePerPage,
  companypages,
  companyPerPage,
  peoplepages,
  peoplePerPage,
} from "../../utils/perPageValue";
import CompactPeopleFinderLoader from "../customeLoader/CompactPeopleFinderLoader";

export default function SearchBar() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("prompt");
  const [urlInput, setUrlInput] = useState("");
  const [descriptionInput, setDescriptionInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const textareaRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const location = useLocation();
  const { searchByUrl, searchByDescription, searchByPrompt, isLoading } =
    useSearch();
  const {
    leads,
    setLeads,
    promptsDataCompany,
    setPromptsDataCompany,
    promptsDataPeople,
    setPromptsDataPeople,
    showPromtData,
    setShowPromtData,
    showUrlData,
    setShowUrlData,
    viewMode,
    setViewMode,
    prompt,
    setPrompt,
    isSearchLoading,
    setIsSearchLoading,
    loadingMessage,
    setLoadingMessage,
    promptInput,
    setPromptInput,
    defaultLocation,
    setDefaultLocation,
  } = useAppContext();
  const dispatch = useDispatch();

  const throttle = (func, delay) => {
    let timeoutId;
    let lastCalled = 0;
    return (...args) => {
      const now = Date.now();
      if (now - lastCalled >= delay) {
        lastCalled = now;
        func(...args);
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          lastCalled = Date.now();
          func(...args);
        }, delay);
      }
    };
  };

  const fetchSuggestions = async (searchTerm) => {
    if (!searchTerm.trim() || searchTerm.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsFetchingSuggestions(true);

    try {
      // 🔹 Actual API call using dispatch
      const response = await dispatch(
        fetchPromptSuggestions(searchTerm.trim()),
      ).unwrap();

      // Handle different response structures
      let suggestionsData = [];
      if (Array.isArray(response)) {
        suggestionsData = response;
      } else if (response?.suggestions && Array.isArray(response.suggestions)) {
        suggestionsData = response.suggestions;
      } else if (response?.data && Array.isArray(response.data)) {
        suggestionsData = response.data;
      } else {
        suggestionsData = [];
      }

      setSuggestions(suggestionsData);
      setSelectedSuggestionIndex(-1);
      setShowDropdown(suggestionsData.length > 0);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
      setShowDropdown(false);
      toast.error("Failed to load suggestions");
    } finally {
      setIsFetchingSuggestions(false);
    }
  };

  // Throttled version of fetchSuggestions
  const throttledFetchSuggestions = useCallback(
    throttle((searchTerm) => fetchSuggestions(searchTerm), 800),
    [],
  );

  // Handle input change
  const handlePromptChange = (e) => {
    const newPrompt = e.target.value;
    setPromptInput(newPrompt);
    setIsTyping(true); // User is typing
    throttledFetchSuggestions(newPrompt);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setPromptInput(suggestion);
    setSuggestions([]);
    setShowDropdown(false);
    setSelectedSuggestionIndex(-1);
    setIsTyping(false); // Reset typing state after selection
    // Focus back on textarea
    textareaRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (
        selectedSuggestionIndex >= 0 &&
        suggestions[selectedSuggestionIndex]
      ) {
        // If a suggestion is selected, use it
        e.preventDefault();
        handleSuggestionClick(suggestions[selectedSuggestionIndex]);
      } else if (e.key === "Enter" && !e.shiftKey) {
        // Prevent default Enter behavior (new line) when not shift+Enter
        e.preventDefault();
        // You can trigger search here if needed
        // handlePromptSearch();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (suggestions.length > 0) {
        setSelectedSuggestionIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev,
        );
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (suggestions.length > 0) {
        setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    const savedPrompt = promptInput;
    localStorage.getItem("lastPrompt");
    if (savedPrompt) {
      setPromptInput(savedPrompt);
    }
  }, []);

  const extractSearchResults = (searchResponse) => {
    if (!searchResponse) return { type: "unknown", data: [] };

    // Support both "result" and "results"
    const r =
      searchResponse?.results || searchResponse?.result || searchResponse || {};

    // COMPANY FORMAT
    if (r.organizations && Array.isArray(r.organizations)) {
      return { type: "companies", data: r.organizations };
    }

    // PEOPLE FORMAT
    if (r.people && Array.isArray(r.people)) {
      return { type: "people", data: r.people };
    }

    // Sometimes raw array
    if (Array.isArray(r)) {
      return { type: "unknown", data: r };
    }

    return { type: "unknown", data: [] };
  };

  const handlePromptSearch = async () => {
    if (!promptInput) {
      toast.error("Please enter a prompt before starting prospecting.");
      return;
    }

    const trimmedPrompt = promptInput.trim();
    // localStorage.setItem("lastPrompt", trimmedPrompt);
    setIsSearchLoading(true);

    setLoadingMessage({
      title:
        "Hold on tight — we’re finding the most relevant results for you...",
      subtitle:
        "Our system is analyzing verified sources to bring you accurate, high-quality leads.",
    });

    try {
      // ------------------ 1️⃣ Start session ------------------
      const sessionPayload = {
        user_id: localStorage.getItem("userId") || "guest",
        metadata: { prompt: trimmedPrompt },
      };

      const sessionResponse = await dispatch(
        startSession(sessionPayload),
      ).unwrap();

      const sessionId = sessionResponse.session_id;
      localStorage.setItem("sessionId", sessionId);

      // ------------------ 2️⃣ Parse Intent ------------------
      const intentResponse = await dispatch(
        parseIntent({
          user_id: sessionResponse.user_id,
          session_id: sessionId,
          prompt: trimmedPrompt,
          website_url: urlInput,
        }),
      ).unwrap();

      const intent = intentResponse?.intent || {};
      const audience = intent.audience || "companies";
      const aiPropmt = intentResponse?.ai_prompt;

      let default_location = "";

      intent.locations.length > 0
        ? (default_location = intent.locations[0])
        : "-";
      setDefaultLocation(default_location);
      let dbResponse = null;
      let searchResponse = null;

      // ======================================================
      // 3️⃣ MAIN LOGIC — COMPANIES
      // ======================================================
      if (audience === "companies") {
        const companyPayload = {
          industry: intent.industries || intent.keywords || trimmedPrompt,
          location: intent.locations || "global",
          limit: companyPerPage,
          prompt: trimmedPrompt,
          skip: 0,
        };

        try {
          dbResponse = await dispatch(fetchCompanies(companyPayload)).unwrap();
        } catch (err) {
          searchResponse = await dispatch(
            seachApi({
              prompt: trimmedPrompt,
              session_id: sessionId,
              use_goapi: true,
              per_page: apolocompanyPerPage,
              pages: companypages,
              include_raw: false,
              website_url: urlInput,
            }),
          ).unwrap();

          dbResponse = null;
        }

        // Fallback if DB < 10
        if (
          !searchResponse &&
          dbResponse?.results &&
          dbResponse.results.length < 20
        ) {
          searchResponse = await dispatch(
            seachApi({
              prompt: trimmedPrompt,
              session_id: sessionId,
              use_goapi: true,
              per_page: apolocompanyPerPage,
              pages: companypages,
              include_raw: false,
              website_url: urlInput,
            }),
          ).unwrap();
        }

        // If DB empty → search
        if (!searchResponse && !dbResponse?.results?.length) {
          searchResponse = await dispatch(
            seachApi({
              prompt: trimmedPrompt,
              session_id: sessionId,
              use_goapi: true,
              per_page: apolocompanyPerPage,
              pages: companypages,
              include_raw: false,
              website_url: urlInput,
            }),
          ).unwrap();
        }
      }

      // ======================================================
      // 3️⃣ MAIN LOGIC — PEOPLE
      // ======================================================
      else if (audience === "people") {
        const people_filters =
          intentResponse?.people_filters || intent?.people_filters || {};

        const personTitles =
          people_filters?.person_titles?.length > 0
            ? people_filters.person_titles
            : people_filters?.person_seniorities?.length > 0
              ? people_filters.person_seniorities
              : ["CEO", "Founder", "HR", "Director"];

        const peoplePayload = {
          prompt: trimmedPrompt,
          job_title: personTitles,
          location: intent.locations || "",
          industry: intent.industries || intent.keywords || trimmedPrompt || "",
          limit: peoplePerPage,
          skip: 0,
        };

        try {
          dbResponse = await dispatch(fetchPeople(peoplePayload)).unwrap();
        } catch (err) {
          searchResponse = await dispatch(
            seachApi({
              prompt: trimmedPrompt,
              session_id: sessionId,
              use_goapi: true,
              per_page: apolopeoplePerPage,
              pages: peoplepages,
              include_raw: false,
              website_url: urlInput,
            }),
          ).unwrap();

          dbResponse = null;
        }

        // Fallback if DB < 10
        if (
          !searchResponse &&
          dbResponse?.results &&
          dbResponse.results.length < 1000
        ) {
          searchResponse = await dispatch(
            seachApi({
              prompt: trimmedPrompt,
              session_id: sessionId,
              use_goapi: true,
              per_page: apolopeoplePerPage,
              pages: peoplepages,
              include_raw: false,
              website_url: urlInput,
            }),
          ).unwrap();
        }

        // If DB empty → search
        if (!searchResponse && !dbResponse?.results?.length) {
          searchResponse = await dispatch(
            seachApi({
              prompt: trimmedPrompt,
              session_id: sessionId,
              use_goapi: true,
              per_page: apolopeoplePerPage,
              pages: peoplepages,
              include_raw: false,
              website_url: urlInput,
            }),
          ).unwrap();
        }
      }

      // ======================================================
      // 4️⃣ FINAL DATA HANDLING (AUTO DETECT COMPANIES / PEOPLE)
      // ======================================================
      let finalData = [];
      let finalType = audience;

      if (searchResponse) {
        const parsed = extractSearchResults(searchResponse);
        finalData = parsed.data;
        finalType = parsed.type !== "unknown" ? parsed.type : audience;
      } else if (dbResponse?.results) {
        finalData = dbResponse.results;
      }

      // Set view + data based on detected type
      if (finalType === "people") {
        setPromptsDataPeople({
          source: searchResponse ? "api" : "db",
          data: finalData,
        });
        setViewMode("people");
      } else {
        setPromptsDataCompany(finalData);
        setViewMode("companies");
      }
      handleSuggestionClick(trimmedPrompt);

      setShowPromtData(true);
      setShowUrlData(false);
      navigate("/search");
    } catch (error) {
      console.error("❌ Prompt search failed:", error);
      toast.error(
        "We couldn't complete your request right now. Please wait a few minutes before trying again.",
      );
    } finally {
      setIsSearchLoading(false);
      setLoadingMessage(null);
    }
  };

  const EXAMPLES = [
    "Founder / Co-Founder / CEO of startups in USA",
    "General Managers of Hotels and Resorts",
    "Tech Entrepreneurs in USA",
    "Importers of Home Decor",
    "Senior Interior Designers",
    "HR Director at SaaS",
    "Importers of Furniture",
  ];

  const pulseVariants = {
    animate: {
      scale: [1, 1.04, 1],
      transition: {
        duration: 1.8,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  const examplesScrollRef = useRef(null);

  const scrollExamples = (direction) => {
    if (!examplesScrollRef.current) return;

    examplesScrollRef.current.scrollBy({
      left: direction === "left" ? -200 : 200,
      behavior: "smooth",
    });
  };

  const isHomeRoute = location.pathname === "/";

  // Small loader component for suggestions
  const SuggestionsLoader = () => (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="w-5 h-5 border-2 border-indigo-200 rounded-full"></div>
          <div className="absolute top-0 left-0 w-5 h-5 border-2 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
        </div>
        <div className="flex-1">
          <div className="h-2 bg-gray-200 rounded-full animate-pulse w-32 mb-2"></div>
          <div className="h-2 bg-gray-100 rounded-full animate-pulse w-24"></div>
        </div>
      </div>
    </div>
  );

  // Dot loader for suggestions
  const DotLoader = () => (
    <div className="px-4 py-3">
      <div className="flex items-center gap-2 text-indigo-600">
        <div className="flex gap-1">
          <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
          <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
          <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce"></div>
        </div>
        <span className="text-xs font-medium">Finding suggestions...</span>
      </div>
    </div>
  );

  return (
    <div className="w-full space-y-4">
      <div className="w-full space-y-1">
        {/* Swipe hint (mobile only) */}
        <p className="flex items-center gap-2 text-xs text-gray-800 text-left max-md:pt-4">
          <img src={arrow} alt="Swipe left" className="w-6 h-6 " />
          <span>Swipe left to see more prompts</span>
        </p>

        <div className="w-full flex items-center gap-2">
          {/* Left Arrow (desktop only) */}
          <button
            onClick={() => scrollExamples("left")}
            className=" md:flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200"
          >
            <ChevronLeft className="max-md:h-5 max-md:w-5 font-bold " />
          </button>

          {/* Scrollable prompts */}
          <div
            ref={examplesScrollRef}
            className="
        flex gap-2 py-2
        overflow-x-auto
        scroll-smooth
        scrollbar-thin
        w-full

        [&::-webkit-scrollbar]:h-1
        [&::-webkit-scrollbar-thumb]:bg-blue-500
        [&::-webkit-scrollbar-track]:bg-transparent
        [&::-webkit-scrollbar-thumb]:rounded-full
      "
          >
            {EXAMPLES.map((ex, index) => (
              <button
                key={index}
                onClick={() => setPromptInput(ex)}
                disabled={isSearchLoading}
                className="flex-shrink-0 px-3 py-1 text-sm bg-gray-100 rounded-md hover:bg-gray-200 whitespace-nowrap"
              >
                {ex}
              </button>
            ))}
          </div>

          {/* Right Arrow (desktop only) */}
          <button
            onClick={() => scrollExamples("right")}
            className=" md:flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200"
          >
            <ChevronRight className="max-md:h-5 max-md:w-5 font-bold " />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 flex max-md:flex-col gap-2 max-md:gap-1 w-full">
          {activeTab === "prompt" && (
            <>
              <div className="w-full space-y-2">
                {/* Layout changes based on route */}
                <div
                  className={`flex gap-2 max-md:gap-1 w-full ${
                    location.pathname === "/"
                      ? "flex-col "
                      : "flex-col md:flex-row md:items-start "
                  }`}
                >
                  {/* 🔹 Prompt Textarea */}
                  <div
                    className={`flex-1 relative ${isHomeRoute ? "z-20" : ""}`}
                  >
                    <textarea
                      ref={textareaRef}
                      placeholder="Importers of packaged food products in USA"
                      value={promptInput}
                      disabled={isSearchLoading}
                      onChange={handlePromptChange}
                      onKeyDown={handleKeyDown}
                      onFocus={() => {
                        if (suggestions.length > 0) {
                          setShowDropdown(true);
                        }
                      }}
                      className={`w-full resize-none bg-white/90 backdrop-blur-sm
                                  border border-indigo-400 shadow-sm rounded-md
                                  px-3 py-2.5 text-sm leading-normal
                                  placeholder:text-gray-400
                                  focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                                  transition-all duration-200
                                  ${
                                    isHomeRoute
                                      ? "h-20 placeholder:text-base text-base"
                                      : "h-10 placeholder:text-sm max-sm:min-h-[48px]"
                                  }
                                `}
                    />

                    {showDropdown && !isSearchLoading && (
                      <>
                        <div
                          ref={dropdownRef}
                          className={`
        absolute z-40 w-full bg-white rounded-xl shadow-2xl 
        border border-gray-100 overflow-hidden
        transition-all duration-200 ease-out
        ${
          isHomeRoute ? " top-full animate-in slide-in-from-top-2" : " top-full"
        }
      `}
                          style={{
                            boxShadow: isHomeRoute
                              ? "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                              : "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                            maxHeight: isHomeRoute ? "280px" : "280px",
                          }}
                        >
                          {/* Header with icon - only show when there are suggestions and not loading */}
                          {!isFetchingSuggestions && suggestions.length > 0 && (
                            <div className="px-4 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                              <div className="flex items-center gap-2 text-xs font-medium text-indigo-600 max-md:flex-col ">
                                <Sparkles className="h-3.5 w-3.5 max-md:hidden" />
                                <span>Suggested prompts</span>
                                <span className="text-gray-700">
                                  (For better results use suggested prompts)
                                </span>
                              </div>
                            </div>
                          )}

                          <div
                            className="overflow-y-auto"
                            style={{
                              maxHeight: isHomeRoute ? "280px" : "280px",
                            }}
                          >
                            {isFetchingSuggestions && (
                              <>
                                {/* Show different loaders based on route */}
                                {isHomeRoute ? (
                                  <div className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                      <div className="relative">
                                        <div className="w-5 h-5 border-2 border-indigo-200 rounded-full"></div>
                                        <div className="absolute top-0 left-0 w-5 h-5 border-2 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
                                      </div>
                                      <div className="flex-1">
                                        <div className="h-2 bg-gray-200 rounded-full animate-pulse w-32 mb-2"></div>
                                        <div className="h-2 bg-gray-100 rounded-full animate-pulse w-24"></div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                      <div className="relative">
                                        <div className="w-4 h-4 border-2 border-indigo-200 rounded-full"></div>
                                        <div className="absolute top-0 left-0 w-4 h-4 border-2 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
                                      </div>
                                      <div className="flex-1">
                                        <div className="h-1.5 bg-gray-200 rounded-full animate-pulse w-28"></div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}

                            {!isFetchingSuggestions &&
                              suggestions.length === 0 &&
                              isTyping &&
                              promptInput.trim().length >= 2 && (
                                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                  No suggestions found
                                </div>
                              )}

                            {!isFetchingSuggestions &&
                              suggestions.map((suggestion, index) => (
                                <div
                                  key={index}
                                  onClick={() =>
                                    handleSuggestionClick(suggestion)
                                  }
                                  className={`
                group px-4 py-3 cursor-pointer transition-all duration-150
                flex items-center justify-between
                ${
                  selectedSuggestionIndex === index
                    ? "bg-indigo-50"
                    : "hover:bg-gray-50"
                }
                ${isHomeRoute ? "border-l-2 border-transparent hover:border-indigo-400" : ""}
              `}
                                >
                                  <span
                                    className={`text-sm ${selectedSuggestionIndex === index ? "text-indigo-700 font-medium" : "text-gray-700"}`}
                                  >
                                    {suggestion}
                                  </span>
                                </div>
                              ))}
                          </div>

                          {/* Footer with hint for home route */}
                          {isHomeRoute &&
                            suggestions.length > 0 &&
                            !isFetchingSuggestions && (
                              <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
                                <span>Esc to close</span>
                              </div>
                            )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* 🔹 URL Input */}
                  <div className="flex-1">
                    <input
                      type="url"
                      placeholder="Enter your company website URL (e.g., example.com)"
                      value={urlInput}
                      disabled={isSearchLoading}
                      onChange={(e) => setUrlInput(e.target.value)}
                      className={`w-full text-sm px-3 bg-white/90 backdrop-blur-sm border border-indigo-400 shadow-sm rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500  placeholder:text-gray-400
                                  ${location.pathname === "/" ? "h-12" : "h-10"}
                                `}
                    />
                  </div>

                  {/* 🔹 Button (Inline only when NOT on "/") */}
                  {location.pathname !== "/" && (
                    <div className="flex items-start max-md:pt-1.5">
                      <Button
                        onClick={handlePromptSearch}
                        disabled={isSearchLoading || !promptInput}
                        className=" h-10 text-sm font-medium main-button shadow-lg hover:shadow-xl rounded-md max-md:px-4"
                      >
                        {isSearchLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Target className="h-4 w-4 mr-2" />{" "}
                            <span className="pr-2">Search</span>
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>

                {/* 🔹 Button below inputs only for "/" route */}
                {location.pathname === "/" && (
                  <div className="flex items-center justify-center">
                    <motion.button
                      onClick={handlePromptSearch}
                      variants={pulseVariants}
                      animate="animate"
                      className={`
                                relative px-6 h-12 max-md:h-10 mt-4 text-sm font-medium rounded-md 
                                flex flex-col justify-center overflow-hidden
                                transition-all duration-300 ease-out opacity-100

                                ${
                                  isSearchLoading
                                    ? "!bg-transparent !shadow-none opacity-100"
                                    : `
                                     main-button
                                      text-white shadow-lg

                                    

                                      /* Shimmer overlay */
                                      before:absolute before:inset-0 before:bg-white/20 
                                      before:translate-x-[-150%]
                                      before:animate-[shimmer_2.5s_linear_infinite]
                                    `
                                }
                              `}
                    >
                      {isSearchLoading ? (
                        <div className="flex flex-col items-center w-full opacity-100">
                          <CompactPeopleFinderLoader />
                        </div>
                      ) : (
                        <div className="flex items-center font-medium text-white">
                          <Target className="h-4 w-4 mr-2" /> Try a Free Search
                        </div>
                      )}
                    </motion.button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
